import {Button} from "@src/ui/views/button";
import {Skeleton} from "@src/ui/views/skeleton";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow
} from "@src/ui/views/table";
import {
	useCreateTimedShuttleOccurrencePolicyMutation,
	useDeleteTimedShuttleOccurrencePolicyMutation,
	useGetTimedShuttlesHistoryQuery,
} from "@src/features/timed-shuttles/redux/timed-shuttles-api-old-endpoint";
import {
	useGetAllTimedShuttlesDefinitionsQuery,
	useCreateTimedShuttleInstanceMutation
} from '@src/features/timed-shuttles/redux/timed-shuttles-api'
import {type ShuttleDefinition} from "@src/features/shuttles/shuttles-models";
import {Check, Plus, X} from "lucide-react";
import * as React from "react";
import {useParams} from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@src/ui/views/dialog";
import { Input } from "@src/ui/views/input";


const DAYS_IN_WEEK: number = 7;


type StartTimeRowProps = {
	spaceId: string
	definitionId: string
	startTime: string
	dailyOccurrences: ReadonlyArray<string | null>
}


type ExistingOccurrenceCellProps = {
	spaceId: string
	definitionId: string
	policyId: string
}


type NonExistingOccurrenceCellProps = {
	spaceId: string
	definitionId: string
	dayOfWeek: number
	startTime: string
}


export function TimedShuttleDefinitionScreen() {
	const routeParams = useParams();
	const selectedSpaceId = routeParams.spaceId!;
	const queryHandle = useGetAllTimedShuttlesDefinitionsQuery({spaceId: selectedSpaceId});
	const [addOpen, setAddOpen] = React.useState(false);
	const [date,    setDate]    = React.useState(new Date().toISOString().slice(0,10));
	const [hours,   setHours]   = React.useState(8);
	const [minutes, setMinutes] = React.useState(0);
  
	const [createInstance, { isLoading: isCreating }] =
	  useCreateTimedShuttleInstanceMutation();
	// console.log('query handle is', queryHandle)
	
	if (queryHandle.isLoading) {
		// todo use skeleton for loading animation
		return (
			<p>
				Loading...
			</p>
		);
	}
	
	if (queryHandle.isError) {
		// todo handle error
		return (
			<p>
				Query Error
			</p>
		);
	}
	
	const allShuttlesDefinitions = queryHandle.data!;
	const selectedDefinitionId = routeParams.definitionId!;
	const shuttleDefinition = allShuttlesDefinitions.shuttleDefinitions
		.find(it => it.definitionId === selectedDefinitionId);
	if (!shuttleDefinition) {
		// todo handle error
		return (
			<p>
				No Shuttle Definitions
			</p>
		);
	}
	
	const startTimeToDailyOccurrence = React.useMemo(() => processOccurrencePolicies(shuttleDefinition),
		[shuttleDefinition]);
	const totalDailyOccurrences = React.useMemo(() => countDailyOccurrences(shuttleDefinition),
		[shuttleDefinition]);
	return (
		<div className="h-max min-h-full p-4">
			 {/* ─── Toolbar ─── */}
			 <div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold">
				Editing {shuttleDefinition.originName} → {shuttleDefinition.destName}
				</h2>
				<Button variant="primary" onClick={() => setAddOpen(true)}>
				Add Instance
				</Button>
			</div>
			<div className="flex flex-row h-max rounded-lg my-2.5">
				<Table
					className="w-3/4 mx-auto p-8 border border-collapse border-gray-200 table-fixed">
					<TableCaption
						className="scroll-m-20 pb-2 text-xl font-semibold tracking-tight caption-top text-center">
						{
							`Editing Shuttle ${shuttleDefinition.originName} -> ${shuttleDefinition.destName}`
						}
					</TableCaption>
					<TableHeader className="bg-gray-50">
						<TableRow>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Start Time
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Sunday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Monday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Tuesday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Wednesday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Thursday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Friday
							</TableHead>
							<TableHead className="size-8 text-center font-bold border border-collapse border-gray-200">
								Saturday
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Array.from(startTimeToDailyOccurrence.entries()).map(([startTime, dailyOccurrences]) => (
								<StartTimeRow
									key={startTime}
									spaceId={selectedSpaceId}
									definitionId={selectedDefinitionId}
									startTime={startTime}
									dailyOccurrences={dailyOccurrences}/>
							)
						)}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableHead
								className="size-8 text-center font-bold italic border border-collapse border-gray-200">
								Day's Total
							</TableHead>
							{
								totalDailyOccurrences.map((curDayTotal, dayNumber) =>
									<TableCell
										key={dayNumber}
										className="size-8 text-center border border-collapse border-gray-200">
										{curDayTotal}
									</TableCell>
								)
							}
						</TableRow>
					</TableFooter>
				</Table>
			</div>
			 {/* ────────── Add Instance Modal ────────── */}
			 <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm space-y-4">
          <DialogTitle>Add One-Off Instance</DialogTitle>

          <label className="block">
            <span className="text-sm">Date</span>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.currentTarget.value)}
              className="mt-1 w-full border rounded"
            />
          </label>

          <div className="flex space-x-2">
            <label className="flex-1 block">
              <span className="text-sm">Hour</span>
              <Input
                type="number"
                min={0}
                max={23}
                value={hours}
                onChange={e => setHours(Number(e.target.value))}
              />
            </label>
            <label className="flex-1 block">
              <span className="text-sm">Minute</span>
              <Input
                type="number"
                min={0}
                max={59}
                value={minutes}
                onChange={e => setMinutes(Number(e.target.value))}
              />
            </label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={isCreating}
              onClick={async () => {
                await createInstance({
                  spaceId: selectedSpaceId,
                  definitionId: selectedDefinitionId,
                  date,
                  hours,
                  minutes,
                }).unwrap();
                setAddOpen(false);
              }}
            >
              {isCreating ? "Adding…" : "Add"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
		</div>
	);
}


function processOccurrencePolicies(shuttleDefinition: ShuttleDefinition): ReadonlyMap<string, ReadonlyArray<string | null>> {
	const startTimeToDailyOccurrences = new Map<string, Array<string | null>>();
	
	for (const occurrencePolicy of shuttleDefinition.occurrencePolicies) {
		const days = startTimeToDailyOccurrences.has(occurrencePolicy.startTime) ?
			startTimeToDailyOccurrences.get(occurrencePolicy.startTime)! :
			new Array<string | null>(DAYS_IN_WEEK).fill(null);
		days[occurrencePolicy.dayOfWeek] = occurrencePolicy.policyId;
		startTimeToDailyOccurrences.set(occurrencePolicy.startTime, days);
	}
	
	const sortedMap = new Map([...startTimeToDailyOccurrences.entries()].sort());
	return sortedMap;
}


function countDailyOccurrences(shuttleDefinition: ShuttleDefinition): ReadonlyArray<number> {
	const totalDailyOccurrences = new Array<number>(DAYS_IN_WEEK).fill(0);
	
	for (const occurrencePolicy of shuttleDefinition.occurrencePolicies) {
		totalDailyOccurrences[occurrencePolicy.dayOfWeek] += 1;
	}
	
	return totalDailyOccurrences;
}


function StartTimeRow(props: StartTimeRowProps) {
	return (
		<TableRow className="h-8 p-0 m-0">
			<TableHead className="size-8 text-center font-semibold border border-collapse border-gray-200">
				{props.startTime}
			</TableHead>
			{
				props.dailyOccurrences.map((occurrenceId, dayNumber) => {
					if (occurrenceId !== null) {
						return (
							<TableCell
								key={`${dayNumber}-${props.startTime}`}
								className="size-8 text-center border border-collapse border-gray-200">
								<ExistingOccurrenceCell
									spaceId={props.spaceId}
									definitionId={props.definitionId}
									policyId={occurrenceId}/>
							</TableCell>
						);
					}
					return (
						<TableCell
							key={`${dayNumber}-${props.startTime}`}
							className="size-8 text-center border border-collapse border-gray-200">
							<NonExistingOccurrenceCell
								spaceId={props.spaceId}
								definitionId={props.definitionId}
								dayOfWeek={dayNumber}
								startTime={props.startTime}/>
						</TableCell>
					);
				})
			}
		</TableRow>
	);
}


function ExistingOccurrenceCell(props: ExistingOccurrenceCellProps) {
	const [isRemoveIconVisible, setIsRemoveIconVisible] = React.useState(false);
	const [deleteOccurrencePolicy, deleteOccurrencePolicyResult] = useDeleteTimedShuttleOccurrencePolicyMutation();
	
	const onMouseEnter = () => {
		setIsRemoveIconVisible(true);
	};
	
	const onMouseLeave = () => {
		setIsRemoveIconVisible(false);
	};
	
	const onClick = () => {
		deleteOccurrencePolicy({
			spaceId: props.spaceId,
			definitionId: props.definitionId,
			policyId: props.policyId
		});
	};
	
	return (
		<Button
			className="size-8 p-0 bg-green-600 hover:bg-destructive hover:text-destructive-foreground"
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			disabled={deleteOccurrencePolicyResult.isLoading}>
			<span className="sr-only">Remove Occurrence</span>
			{
				deleteOccurrencePolicyResult.isLoading ?
					<Skeleton className="size-4"/> :
					isRemoveIconVisible ?
						<X className="size-4"/> :
						<Check className="size-4"/>
			}
		</Button>
	);
}


function NonExistingOccurrenceCell(props: NonExistingOccurrenceCellProps) {
	const [isAddIconVisible, setIsAddIconVisible] = React.useState(false);
	const [createOccurrencePolicy, createOccurrencePolicyResult] = useCreateTimedShuttleOccurrencePolicyMutation();
	
	const onMouseEnter = () => {
		setIsAddIconVisible(true);
	};
	
	const onMouseLeave = () => {
		setIsAddIconVisible(false);
	};
	
	const onClick = () => {
		createOccurrencePolicy({
			spaceId: props.spaceId,
			definitionId: props.definitionId,
			dayOfWeek: props.dayOfWeek,
			startTime: props.startTime
		});
	};
	
	return (
		<Button
			className="size-8 p-0 bg-transparent hover:bg-blue-800"
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			disabled={createOccurrencePolicyResult.isLoading}>
			<span className="sr-only">Add Occurrence</span>
			{
				createOccurrencePolicyResult.isLoading ?
					<Skeleton className="size-4"/> :
					isAddIconVisible && <Plus className="size-4"/>
			}
		</Button>
	);
}
